import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Footprints } from 'lucide-react';

const StudentReadinessProgram: React.FC = () => {
    const navigate = useNavigate();

    const handleContactClick = () => {
        navigate('/eligibility');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <div className="bg-[#009B8F] text-white py-16 mb-16">
                <div className="max-w-7xl mx-auto px-6">
                    <h1 className="text-4xl md:text-5xl font-serif mb-4">Student Readiness Program</h1>
                    <p className="text-xl text-white/90 max-w-2xl">
                        Preparing you for every aspect of international education.
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6">
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-[#009B8F]/10 rounded-full">
                        <Footprints className="text-[#009B8F] w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-serif text-[#011E26]">Complete Preparation</h2>
                </div>

                <div className="prose prose-lg text-slate-600 max-w-none">
                    <p className="mb-4">
                        Studying abroad requires more than academic skills it also demands adaptability, confidence, and practical knowledge to thrive in a new environment.
                        At Nex Overseas, our Student Readiness Program is designed to prepare students for every aspect of international education, ensuring a smooth transition and a successful experience abroad.
                    </p>

                    <h3 className="text-2xl font-serif text-[#011E26] mb-4">Key Focus Areas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div className="bg-gray-50 p-5 rounded-lg border border-gray-100">
                            <h4 className="font-bold text-[#009B8F] mb-2">Cultural Adaptation</h4>
                            <p className="text-sm">Understanding and adjusting to new social and academic environments.</p>
                        </div>
                        <div className="bg-gray-50 p-5 rounded-lg border border-gray-100">
                            <h4 className="font-bold text-[#009B8F] mb-2">Study Skills</h4>
                            <p className="text-sm">Time management, note-taking, and effective learning strategies.</p>
                        </div>
                        <div className="bg-gray-50 p-5 rounded-lg border border-gray-100">
                            <h4 className="font-bold text-[#009B8F] mb-2">Communication Skills</h4>
                            <p className="text-sm">Academic and social communication to interact confidently with peers and faculty.</p>
                        </div>
                        <div className="bg-gray-50 p-5 rounded-lg border border-gray-100">
                            <h4 className="font-bold text-[#009B8F] mb-2">Practical Preparation</h4>
                            <p className="text-sm">Guidance on university procedures, campus life, and local regulations.</p>
                        </div>
                    </div>

                    <p className="mb-6">
                        Our personalised approach ensures each student is confident, independent, and well-prepared to face the challenges and opportunities of studying overseas.
                    </p>

                    <div className="bg-[#011E26] text-white p-8 rounded-lg mb-8">
                        <h3 className="text-xl font-bold mb-4 border-b border-[#009B8F] pb-2 inline-block">Why Choose Our Student Readiness Program?</h3>
                        <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-[#009B8F] mt-1" />
                                <span>Holistic preparation for academic and social life abroad</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-[#009B8F] mt-1" />
                                <span>Practical tips for independent living and self-management</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-[#009B8F] mt-1" />
                                <span>Guidance to enhance adaptability, confidence, and cultural awareness</span>
                            </li>
                        </ul>
                    </div>

                    <p className="mb-8 font-medium text-[#011E26]">
                        Start your journey fully prepared! Contact us today to learn more about the Student Readiness Program and take the first step toward a successful study abroad experience.
                    </p>

                    <button
                        onClick={handleContactClick}
                        className="bg-[#009B8F] text-white px-8 py-3 font-bold uppercase tracking-widest hover:bg-[#00857a] transition-colors rounded-sm shadow-lg"
                    >
                        Contact Us
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StudentReadinessProgram;
